import { Anchor, Button, Select, SimpleGrid, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { IconAlertTriangle, IconCircleCheck, IconClock, IconWorldWww } from "@tabler/icons-react";
import { useMemo } from "react";
import { Link } from "react-router";
import { semanticColor } from "@how/design-system";
import {
  DataTable,
  FilterBar,
  PageHeader,
  StatCard,
  StatusBadge,
  useSearchParamState,
} from "@how/ui";
import { isAuthorizationError } from "../../lib/query-client";
import { usePlatformRole } from "../auth/platform-role";
import {
  DOMAIN_STATUS_LABELS,
  DOMAIN_STATUSES,
  INCLUDED_HOSTNAMES,
  isDomainStatus,
  isTenantSurface,
  recheckErrorMessage,
  SURFACE_LABELS,
  SURFACES,
  useDomainCounts,
  useDomains,
  useRecheckDomain,
  type DomainRow,
  type DomainSort,
} from "./domains";

const PAGE_SIZE = 25;
const SORTABLE = new Set<DomainSort["id"]>(["hostname", "status", "created_at"]);

/** Cloudflare's two states in one cell: the hostname's, then the certificate's. */
export function cloudflareLine(row: Pick<DomainRow, "cf_status" | "cf_ssl_status">): string {
  if (row.cf_status === null) return "Not created yet";
  const words = (s: string) => s.replace(/_/g, " ");
  return `${words(row.cf_status)} · certificate ${words(row.cf_ssl_status ?? "not started")}`;
}

function CheckNow({ row }: { readonly row: DomainRow }) {
  const recheck = useRecheckDomain();
  return (
    <Button
      variant="default"
      size="compact-xs"
      style={{ flexShrink: 0, whiteSpace: "nowrap" }}
      loading={recheck.isPending}
      aria-label={`Check ${row.hostname} now`}
      onClick={(event) => {
        event.stopPropagation();
        recheck.mutate(row.id, {
          onSuccess: () => {
            notifications.show({
              color: semanticColor("info"),
              title: "Checking now",
              message: `${row.hostname} is first in the queue. It is looked at within a minute.`,
            });
          },
          onError: (error) => {
            notifications.show({
              color: semanticColor("danger"),
              title: "Could not check",
              message: recheckErrorMessage(error),
            });
          },
        });
      }}
    >
      Check now
    </Button>
  );
}

function columnsFor(canAct: boolean): ColumnDef<DomainRow>[] {
  const columns: ColumnDef<DomainRow>[] = [
    {
      accessorKey: "hostname",
      header: "Address",
      cell: (ctx) => (
        <Stack gap={0}>
          <Text size="sm" fw={500} style={{ overflowWrap: "anywhere" }}>
            {ctx.row.original.hostname}
          </Text>
          {ctx.row.original.is_primary ? (
            <Text size="xs" c="dimmed">
              The site's main address
            </Text>
          ) : null}
        </Stack>
      ),
    },
    {
      accessorKey: "surface",
      header: "Used for",
      enableSorting: false,
      cell: (ctx) => <Text size="sm">{SURFACE_LABELS[ctx.row.original.surface]}</Text>,
    },
    {
      id: "church",
      header: "Church",
      enableSorting: false,
      cell: (ctx) => (
        <Anchor component={Link} to={`/tenants/${ctx.row.original.tenant_id}`} size="sm">
          {ctx.row.original.tenant?.name ?? "Unknown church"}
        </Anchor>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (ctx) => (
        <StatusBadge
          domain="domain_status"
          value={ctx.row.original.status}
          label={DOMAIN_STATUS_LABELS[ctx.row.original.status]}
          // The words are the state; a truncated badge would say nothing.
          style={{ flexShrink: 0, minWidth: "max-content" }}
        />
      ),
    },
    {
      id: "cloudflare",
      header: "Cloudflare",
      enableSorting: false,
      cell: (ctx) => (
        <Stack gap={0} maw={340} style={{ whiteSpace: "normal" }}>
          <Text size="sm">{cloudflareLine(ctx.row.original)}</Text>
          {ctx.row.original.last_error !== null && ctx.row.original.status !== "active" ? (
            <Text size="xs" c="dimmed" style={{ overflowWrap: "anywhere" }}>
              {ctx.row.original.last_error}
            </Text>
          ) : null}
        </Stack>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Added",
      meta: { numeric: true },
      cell: (ctx) => new Date(ctx.row.original.created_at).toLocaleDateString(),
    },
  ];
  if (canAct) {
    columns.push({
      id: "actions",
      header: "",
      enableSorting: false,
      cell: (ctx) =>
        ctx.row.original.status === "active" || ctx.row.original.status === "disabled" ? null : (
          <CheckNow row={ctx.row.original} />
        ),
    });
  }
  return columns;
}

/**
 * Domains (ADR 0051): every church's own address and where it stands, for DCX Logic. Churches connect their domains
 * themselves in the staff dashboard; this page is for seeing them all, finding the ones that are stuck, and (a super
 * admin) asking for a check. Support reads.
 */
export function DomainsPage() {
  const role = usePlatformRole();
  const [search, setSearch] = useSearchParamState("q");
  const [statusParam, setStatusParam] = useSearchParamState("status");
  const [surfaceParam, setSurfaceParam] = useSearchParamState("surface");
  const [page, setPage] = useSearchParamState("page", "1");
  const [sortParam, setSortParam] = useSearchParamState("sort", "created_at.desc");
  const [sortId, sortDir] = sortParam.split(".");
  const sort: DomainSort = {
    id: SORTABLE.has(sortId as DomainSort["id"]) ? (sortId as DomainSort["id"]) : "created_at",
    desc: sortDir === "desc",
  };
  const status = isDomainStatus(statusParam) ? statusParam : "";
  const surface = isTenantSurface(surfaceParam) ? surfaceParam : "";
  const pageIndex = Math.max(0, Number.parseInt(page, 10) - 1 || 0);
  const domains = useDomains({ search, status, surface, pageIndex, pageSize: PAGE_SIZE, sort });
  const counts = useDomainCounts();
  const sorting = useMemo<SortingState>(
    () => [{ id: sort.id, desc: sort.desc }],
    [sort.id, sort.desc],
  );
  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex, pageSize: PAGE_SIZE }),
    [pageIndex],
  );
  const columns = useMemo(() => columnsFor(role.data === "super_admin"), [role.data]);

  const tableStatus = domains.isPending
    ? "loading"
    : domains.isError
      ? isAuthorizationError(domains.error)
        ? "denied"
        : "error"
      : "ready";
  const total = counts.data?.total ?? 0;
  const attention = counts.data?.attention ?? 0;
  const filtered = search.trim() !== "" || status !== "" || surface !== "";

  return (
    <>
      <PageHeader
        title="Domains"
        description="Churches' own addresses: their websites, and the addresses their staff and members sign in on. Each church connects them in its dashboard; HoW issues the certificate."
      />
      <Stack gap="xl">
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
          <StatCard
            emphasis="hero"
            label="Custom domains"
            value={total.toLocaleString()}
            loading={counts.isPending}
            icon={<IconWorldWww size={20} />}
            hint={
              total <= INCLUDED_HOSTNAMES
                ? `${String(INCLUDED_HOSTNAMES - total)} of Cloudflare's ${String(INCLUDED_HOSTNAMES)} included hostnames left.`
                : `${String(total - INCLUDED_HOSTNAMES)} beyond Cloudflare's ${String(INCLUDED_HOSTNAMES)} included, at $0.10 a month each.`
            }
          />
          <StatCard
            label="Live"
            value={(counts.data?.live ?? 0).toLocaleString()}
            loading={counts.isPending}
            icon={<IconCircleCheck size={18} />}
            role="success"
            hint="Answering with a certificate."
          />
          <StatCard
            label="On their way"
            value={(counts.data?.waiting ?? 0).toLocaleString()}
            loading={counts.isPending}
            icon={<IconClock size={18} />}
            role="info"
            hint="Waiting for the church's record or the certificate."
          />
          <StatCard
            label="Need attention"
            value={attention.toLocaleString()}
            loading={counts.isPending}
            icon={<IconAlertTriangle size={18} />}
            role={attention > 0 ? "danger" : "neutral"}
            hint={attention > 0 ? "Cloudflare refused or timed out." : "None stuck."}
          />
        </SimpleGrid>
        <div>
          <FilterBar
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(undefined);
            }}
            searchPlaceholder="Search addresses"
            filters={
              <>
                <Select
                  aria-label="Status"
                  placeholder="Any status"
                  clearable
                  value={status === "" ? null : status}
                  onChange={(value) => {
                    setStatusParam(value ?? undefined);
                    setPage(undefined);
                  }}
                  data={DOMAIN_STATUSES.map((s) => ({ value: s, label: DOMAIN_STATUS_LABELS[s] }))}
                  w={200}
                />
                <Select
                  aria-label="Used for"
                  placeholder="Any use"
                  clearable
                  value={surface === "" ? null : surface}
                  onChange={(value) => {
                    setSurfaceParam(value ?? undefined);
                    setPage(undefined);
                  }}
                  data={SURFACES.map((s) => ({ value: s, label: SURFACE_LABELS[s] }))}
                  w={200}
                />
              </>
            }
          />
          <DataTable
            label="Custom domains"
            columns={columns}
            rows={domains.data?.rows ?? []}
            totalRows={domains.data?.total ?? 0}
            status={tableStatus}
            pagination={pagination}
            onPaginationChange={(updater) => {
              const next = typeof updater === "function" ? updater(pagination) : updater;
              setPage(String(next.pageIndex + 1));
            }}
            sorting={sorting}
            onSortingChange={(updater) => {
              const next = typeof updater === "function" ? updater(sorting) : updater;
              const first = next[0];
              setSortParam(
                first === undefined ? undefined : `${first.id}.${first.desc ? "desc" : "asc"}`,
              );
            }}
            getRowId={(row) => row.id}
            minWidth={1180}
            emptyTitle={filtered ? "No domains match" : "No church has connected a domain yet"}
            emptyDescription={
              filtered
                ? "Try a different search, status or use."
                : "A church's owner connects one in the staff dashboard, under Website, Domain, or Settings, Sign-in addresses."
            }
            errorMessage="Could not load domains."
            onRetry={() => {
              void domains.refetch();
            }}
          />
        </div>
      </Stack>
    </>
  );
}
